'use client';

import { useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Image,
  Center,
  Flex,
} from '@chakra-ui/react';
import { HLLogo } from '../ui/svgs/AppDownload/HLLogo';
import { WildmetaIcon } from '../ui/svgs/wildmetaIcon';
import { IosStore } from '../ui/svgs/iosStore';
import { GooglePlay } from '../ui/svgs/googlePlay';
import { SiteIcon } from '../ui/svgs/siteIcon';
import { ApkDownloadResponse } from '@/lib/services/account.service';
import { toaster } from '../ui/toaster';
import { useTranslations } from 'next-intl';

interface DownloadPageContentForPCProps {
  apkDownloadData: ApkDownloadResponse | null;
}

export default function DownloadPageDesktopLayout({ apkDownloadData }: DownloadPageContentForPCProps) {
  const t = useTranslations('DownloadPage');

  useEffect(() => {
    document.title = t('title');
  }, [t]);

  const handleAppStoreDownload = () => {
    window.open('https://apps.apple.com/us/app/wildmeta/id6747332698', '_blank');
  };

  const handleGooglePlayDownload = () => {
    window.open('https://play.google.com/store/apps/details?id=io.wildmeta.app', '_blank');
  };

  const handleOfficialWebsite = () => {
    window.open('https://wildmeta.ai/', '_blank');
  };

  const handleDownloadApk = () => {
    if (apkDownloadData?.url) {
      window.open(apkDownloadData.url, '_blank');
    } else {
      toaster.error({
        title: t('apkNotAvailable'),
      });
    }
  };

  return (
    <Center 
      minH="100vh" 
      w="full" 
      bg="white"
    >
      <Center
        w="full" 
        h="full" 
        style={{
          background: 'radial-gradient(45% 100% at 100% 110%, #4BD5C1 0%, rgba(255, 255, 255, 0.70) 100%)'
        }}
      >
        <Flex 
          w="full" 
          h="100vh" 
          maxW={'1400px'}
          m={'0 auto'}
        >
          {/* Left Panel */}
          <VStack
            flex="0 0 auto"
            gap={7}
            align="stretch"
            px={20}
            py={10}
            my={6}
            borderRight="1px solid"
            borderColor="border"
            justify="flex-start"
          >
            {/* Logo and Navigation */}
            <HStack gap={8} align="center">
              <Image
                src="/images/logo.png"
                alt="Wildmeta Logo"
                w={'7.5rem'}
                h={'7.5rem'}
                objectFit="contain"
              />
              
              <VStack gap={4} align="flex-start" flexShrink={0}>
                <WildmetaIcon />
                <HStack gap={6} align="center">
                  <Text
                    fontSize="lg"
                    fontWeight="black"
                    color="black"
                    whiteSpace="nowrap"
                  >
                    {t('discover')}
                  </Text>
                  <Text
                    fontSize="lg"
                    fontWeight="black"
                    color="black"
                    whiteSpace="nowrap"
                  >
                    {t('learn')}
                  </Text>
                  <Text
                    fontSize="lg"
                    fontWeight="black"
                    color="black"
                    whiteSpace="nowrap"
                  >
                    {t('trade')}
                  </Text>
                </HStack>
              </VStack>
            </HStack>

            {/* Download Buttons */}
            <VStack gap={4} w="full">
              <HStack justify="space-between" w="full">
                <Button
                  onClick={handleAppStoreDownload}
                  h="3.375rem"
                  w="11.25rem"
                  bg="bg.t0"
                  borderRadius="0.5rem"
                  border="1px solid"
                  borderColor="text.t1/50"
                  _hover={{ bg: "bg.t0/80" }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <IosStore />
                </Button>

                <Button
                  onClick={handleGooglePlayDownload}
                  h="3.375rem"
                  w="11.25rem"
                  bg="bg.t0"
                  borderRadius="0.5rem"
                  border="1px solid"
                  borderColor="text.t1/50"
                  _hover={{ bg: "bg.t0/80" }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <GooglePlay />
                </Button>
              </HStack>

              {/* APK Download Button */}
              {/* {apkDownloadData && (
                <Button
                  onClick={handleDownloadApk}
                  h="3.375rem"
                  w="full"
                  bg="bg.t0"
                  borderRadius="0.5rem"
                  border="1px solid"
                  borderColor="text.t1/50"
                  _hover={{ bg: "bg.t0/80" }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Text fontWeight="medium">{t('downloadApk')}</Text>
                </Button>
              )} */}
            </VStack>

            {/* Phone Preview */}
            <Box
              bg="bg.card4"
              borderRadius="2.5rem"
              px="3rem"
              pt={12}
              w="full"
              display="flex"
              justifyContent="center"
              flex={1}
              flexShrink={0}
            >
              <Box
                w="full"
                h="full"
                overflow="hidden"
                borderRadius="1.5rem 1.5rem 0 0"
                bg="center"
                bgSize="cover"
                bgImage="url('/images/download/preview.png')"
                bgPos="center top"
              />
            </Box>
          </VStack>

          {/* Right Panel */}
          <Flex
            flex={1}
            flexBasis={"32rem"}
            flexShrink={0}
            direction="column"
            align="center"
            justify="center"
            gap={8}
            h="100vh"
          >
            <VStack gap={6} align="center" w="full">
              <VStack gap={4} align="center" w="full">
                <Text
                  fontSize="1.75rem"
                  fontWeight={900}
                  color="bg.t0"
                  textAlign="center"
                  lineHeight="normal"
                >
                  {t('scanWithPhone')}
                  <br />
                  {t('scanDescription')}
                </Text>
              </VStack>
              
              {/* QR Code Container */}
              <Box
                bg="bg.card4"
                border="1px solid rgba(0,0,0,0.1)"
                borderRadius="1rem"
                p={10}
              >
                <Box
                  w="12.5rem"
                  h="12.5rem"
                  position="relative"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Image
                    src="/images/download/qrcode.png"
                    alt="QR Code"
                    w="full"
                    h="full"
                    borderRadius="0.5rem"
                    objectFit="cover"
                    transform="skewX(1deg)"
                  />
                </Box>
              </Box>
            </VStack>

            {/* Official Website Button */}
            <Button
              onClick={handleOfficialWebsite}
              bg="bg.t0"
              color="text.t1"
              borderRadius="0.75rem"
              px={6}
              py={3}
              _hover={{ bg: "bg.t0/80" }}
            >
              <HStack gap={2}>
                <SiteIcon />
                <Text fontSize="sm" fontWeight="medium">{t('officialWebsite')}</Text>
                <Image
                  src="/images/download/arrow-right-up.png"
                  alt="Arrow"
                  w={5}
                  h={5}
                />
              </HStack>
            </Button>

            {/* Footer with Hyperliquid Logo */}
            <HStack
              gap={1}
              align="center"
            >
              <Text fontSize={'xs'} color="text.footnote">{t('theHyperliquidSuperApp')}</Text>
              <Box mx={1}>
                <HLLogo />
              </Box>
            </HStack>
          </Flex>
        </Flex>
      </Center>
    </Center>
  );
}